import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './Constants'; // Assuming this constant is defined elsewhere
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
// import { useEffect } from 'react'; // Not strictly needed for this component, but keeping it commented out

// A custom color class for the yellow input background to match the image
// NOTE: You might need to add this color to your tailwind.config.js for full support
// For this example, we use a direct hex code for 'input-yellow' for clarity in the classname
const INPUT_BG_COLOR = '#fff8e1'; 
const INPUT_BG_HOVER = '#fff6de'; 


// Custom component for the Eye icon (for password visibility toggle)
const EyeIcon = ({ onClick }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className="h-5 w-5 text-gray-500 cursor-pointer" 
    fill="none" 
    viewBox="0 0 24 24" 
    stroke="currentColor" 
    onClick={onClick}
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
    />
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
    />
  </svg>
);


const LoginForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false); // State for password visibility

  const [formData, setFormData] = useState({
    email: '', // Backend still expects 'email' based on your logic
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]); // Kept for consistency with original logic

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Using 'email' for validation as per your backend logic
    if (!formData.email) {
      newErrors.email = 'Username (Email) is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Username must be a valid email format';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
      
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Send GET request to Laravel login endpoint
      // NOTE: Using GET for login is unusual and less secure; POST is standard.
      const response = await axios.get(`${API_BASE_URL}/api/login`, { params: formData });

      // Handle successful login
      setUsers(response.data);
      console.log('Users fetched:', response.data);
      
      // Filter users with current form data
      const filteredUsers = response.data.filter(user => 
        user.email === formData.email && user.password === formData.password
      );
      
      if (filteredUsers.length > 0) {
        setIsLoading(false);
        // Set cookies as per original logic
        Cookies.set('mycookie1', `${formData.email}`, {secure: true,sameSite: 'strict'});
        Cookies.set('mycookie2', `${formData.password}`, {secure: true,sameSite: 'strict'});
        Cookies.set('mycookie3', `${filteredUsers[0].id}`, {secure: true,sameSite: 'strict'});
        Cookies.set('mycookie4', `${filteredUsers[0].role}`, {secure: true,sameSite: 'strict'});
      
        // Redirect logic
        switch (filteredUsers[0].role) {
          case 'admin':
            navigate('/simpletabs');
            break;
          case 'tutor':
            navigate(`tutor-dashboard/${filteredUsers[0].id}`);
            break;
          case 'student':
            navigate(`student-dashboard/${filteredUsers[0].id}`);
            break;
          default:
            navigate('/login');
        }
      } else {
        setErrors({ 
          general: 'Invalid username or password' 
        });
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ 
        general: error.response?.data?.message || 'Login failed. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // Main Container: Full screen height, Flex for center alignment
    <div className="min-h-screen flex items-center justify-center bg-[#fefdfd] p-4">
      {/* Login Card Container: Max width for desktop, two columns */}
      <div className="flex w-full max-w-4xl min-h-[550px] shadow-2xl rounded-xl overflow-hidden">
        
        {/* Left Panel: Aesthetic side, matches image background */}
        <div className="hidden lg:flex flex-col justify-start p-10 w-1/2 bg-[#fdfafb] relative">
          
          {/* Logo and Slogan */}
          <div className="mb-10 z-10">
            <div className="text-3xl font-extrabold text-[#ff8040] mb-2">
              Nova Online Tuition<sup className="text-xl align-super">&reg;</sup>
            </div>
            <div className="text-sm font-semibold text-gray-700">
              One to One Learning Platform
            </div>
          </div>

          {/* Sign In Text */}
          <div className="text-4xl font-light leading-snug mb-4 z-10">
            Sign in to join the
            <div className="text-6xl font-extrabold text-[#ff575c] drop-shadow-lg mt-2">
             Academic Revolution
            </div>
          </div>

          {/* Illustration/Clouds - Using an image tag for the main illustration is best practice */}
          <div className="absolute inset-0 z-0">
            {/* Image Placeholder - Replace with your actual image path or a component */}
            <div className="absolute bottom-0 right-0 w-full h-full">
              {/* This is a placeholder for the boy and the red triangle/clouds */}
              <div className="absolute right-[-10px] top-[20%] w-[50%] h-[50%]">
                {/* Replace  with a proper image component or public/asset path */}
                 
              </div>
              <div className="absolute bottom-0 left-0">
                {/* Clouds at the bottom left */}
                              </div>
            </div>
          </div>

        </div>

        {/* Right Panel: Login Form */}
        <div className="w-full lg:w-1/2 bg-white p-8 sm:p-12 flex flex-col justify-center">
          
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">
            Welcome!
          </h2>

          {/* General Error Message */}
          {errors.general && (
            <div role="alert" className="alert alert-error text-white bg-red-500 border-0 mb-4 shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{errors.general}</span>
            </div>
          )}

          {/* The form structure */}
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Username Input (Email) */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold text-gray-700">Username</span> 
              </label>
              <input
                type="text" // Using 'text' to match the generic 'Username' label
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Emai ID" // Placeholder from the image
                // Custom classes to achieve the light yellow background and minimal border
                className={`input w-full font-bold text-lg text-gray-800 focus:outline-none focus:ring-0 ${
                  errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-[${INPUT_BG_COLOR}] hover:bg-[${INPUT_BG_HOVER}] focus:border-red-500'
                }`}
                style={{ backgroundColor: INPUT_BG_COLOR, border: '1px solid #e5e7eb' }} // Inline style for guaranteed color
              />
              {errors.email && (
                <label className="label">
                  <span className="label-text-alt text-red-500">{errors.email}</span>
                </label>
              )}
            </div>
            
            {/* Password Input */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold text-gray-700">Password</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  // Custom classes for the light yellow background and minimal border
                  className={`input w-full font-bold text-lg text-gray-800 pr-10 focus:outline-none focus:ring-0 ${
                    errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-[${INPUT_BG_COLOR}] hover:bg-[${INPUT_BG_HOVER}] focus:border-red-500'
                  }`}
                  style={{ backgroundColor: INPUT_BG_COLOR, border: '1px solid #e5e7eb' }} // Inline style for guaranteed color
                />
                <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <EyeIcon onClick={togglePasswordVisibility} />
                </span>
              </div>
              {errors.password && (
                <label className="label">
                  <span className="label-text-alt text-red-500">{errors.password}</span>
                </label>
              )}
            </div>
            
            {/* Sign In Button */}
            <div className="form-control mt-8">
              <button 
                type="submit" 
                // Custom red background and shadow for the button
                className={`btn w-full bg-[#ff0000] text-white font-extrabold text-xl border-none shadow-xl hover:bg-[#e60000] ${
                  isLoading ? 'loading' : ''
                }`}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
            
            {/* Sign in using OTP Link */}
            <div className="text-center pt-2">
              <a 
                href="#" // Replace with your actual OTP link
                className="text-gray-500 hover:text-[#ff0000] text-sm font-semibold"
                onClick={(e) => { e.preventDefault(); console.log('Sign in using OTP clicked'); }}
              >
                Sign in using OTP
              </a>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;